import React, { useEffect, useState } from 'react';
import { ShieldCheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { travelCost } from '../../utils/event';
import { baht, bookingFee } from '../../utils/format';
interface OfferSheetProps {
  open: boolean;
  onClose: () => void;
  matchId: string;
}
export function OfferSheet({
  open,
  onClose,
  matchId
}: OfferSheetProps) {
  const {
    matches,
    getEvent,
    getPerformer,
    sendOffer
  } = useMarketplace();
  const match = matches.find(m => m.id === matchId);
  const event = match ? getEvent(match.eventId) : undefined;
  const performer = match ? getPerformer(match.performerId) : undefined;
  const openSlots = event?.slots.filter(s => !s.filledBy) ?? [];
  const preferred = openSlots.find(s => s.kind === performer?.actType) ?? openSlots[0];
  const [slotId, setSlotId] = useState(preferred?.id ?? '');
  const [fee, setFee] = useState(0);
  const [travel, setTravel] = useState(0);
  const [setLength, setSetLength] = useState(60);
  const [arrival, setArrival] = useState('18:00');
  useEffect(() => {
    if (!open || !performer || !preferred) return;
    setSlotId(preferred.id);
    setFee(Math.min(performer.price, preferred.budget));
    setTravel(travelCost(performer, performer.distanceKm));
    setSetLength(preferred.setLength);
    if (event) {
      const [h, m] = event.startTime.split(':').map(Number);
      setArrival(`${String(Math.max(0, h - 1)).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!event || !performer) return null;
  const subtotal = fee + travel;
  const serviceFee = bookingFee(subtotal);
  const field = 'mt-1.5 h-12 w-full rounded-sm border border-line bg-surface px-4 text-[15px] font-semibold focus:border-ink focus:outline-none';
  return <Sheet open={open} onClose={onClose} title={`Offer to ${performer.name}`} footer={<Button variant="accent" size="lg" fullWidth disabled={!slotId} onClick={() => {
    sendOffer(matchId, {
      slotId,
      fee,
      travel,
      setLength,
      arrival
    });
    toast.success('Offer sent', {
      description: `${performer.name} will get it in the chat.`
    });
    onClose();
  }} data-id="element-180" data-mp="1gr9db764rs66">
          Send offer · {baht(subtotal + serviceFee)}
        </Button>} data-id="element-179" data-mp="1lq2r70pbxl6v">
      {openSlots.length === 0 ? <p className="py-6 text-[15px] text-ink-2" data-id="element-181" data-mp="1btznhqetxeht">Every slot for {event.name} is already booked.</p> : <div className="space-y-5 pt-1" data-id="element-182" data-mp="171g2i8dtbh2z">
          <fieldset data-id="element-183" data-mp="jcnbmqc1rzf8">
            <legend className="text-[15px] font-bold" data-id="element-184" data-mp="jc5590vo84tl">Slot</legend>
            <div className="mt-2 grid gap-2" data-id="element-185" data-mp="jaajjkcks8og">
              {openSlots.map(s => <button key={s.id} type="button" aria-pressed={slotId === s.id} onClick={() => {
            setSlotId(s.id);
            setSetLength(s.setLength);
          }} className={twMerge('flex items-center justify-between rounded-sm border px-4 py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', slotId === s.id ? 'border-ink bg-surface' : 'border-line bg-surface hover:border-ink-3')} data-id="element-186" data-mp="ebhhqp6ni644">
                  <span data-id="element-187" data-mp="2zpnp03auvneb">
                    <span className="block font-bold" data-id="element-188" data-mp="2zq7atv3mmvxm">{s.kind}</span>
                    <span className="block text-sm text-ink-3" data-id="element-189" data-mp="34p0jna7yxyrg">{s.setLength} min · budget {baht(s.budget)}</span>
                  </span>
                  <span className={twMerge('h-5 w-5 rounded-full border-2', slotId === s.id ? 'border-[6px] border-ink' : 'border-line')} data-id="element-190" data-mp="1oa8tl3yap3lb" />
                </button>)}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3" data-id="element-191" data-mp="28m4n3z88baf8">
            <label className="block text-sm font-bold" data-id="element-192" data-mp="23sigoosns2rv">
              Fee (฿)
              <input type="number" min={0} value={fee} onChange={e => setFee(Number(e.target.value))} className={field} data-id="element-193" data-mp="1zrghg7yqrqpw" />
            </label>
            <label className="block text-sm font-bold" data-id="element-194" data-mp="1uuq1rlqumx50">
              Travel (฿)
              <input type="number" min={0} value={travel} onChange={e => setTravel(Number(e.target.value))} className={field} data-id="element-195" data-mp="2rhuqnqvt8ev8" />
            </label>
            <label className="block text-sm font-bold" data-id="element-196" data-mp="2iihc4jstx03n">
              Set length (min)
              <input type="number" min={15} step={15} value={setLength} onChange={e => setSetLength(Number(e.target.value))} className={field} data-id="element-197" data-mp="2ikalw7yqo0fv" />
            </label>
            <label className="block text-sm font-bold" data-id="element-198" data-mp="174vqh8m6hgmv">
              Arrival
              <input type="time" value={arrival} onChange={e => setArrival(e.target.value)} className={field} data-id="element-199" data-mp="1236d3mbhgqvm" />
            </label>
          </div>
          <dl className="space-y-2 rounded-md bg-surface-2 p-4 text-[15px]" data-id="element-200" data-mp="ry6itm7kb3ij">
            <div className="flex justify-between" data-id="element-201" data-mp="wzvvt7echr99">
              <dt className="text-ink-2" data-id="element-202" data-mp="11xxbdickb8fz">{performer.name} receives</dt>
              <dd className="tabular font-bold" data-id="element-203" data-mp="11wm7zjn142pe">{baht(subtotal)}</dd>
            </div>
            <div className="flex justify-between" data-id="element-204" data-mp="16r9ytp5i9975">
              <dt className="text-ink-2" data-id="element-205" data-mp="1ark40zlh3vky">Booking fee (6%, min ฿150)</dt>
              <dd className="tabular font-bold" data-id="element-206" data-mp="1fpvcp701801l">{baht(serviceFee)}</dd>
            </div>
            <div className="flex justify-between border-t border-line pt-2" data-id="element-207" data-mp="1froo0zllb8fx">
              <dt className="font-bold" data-id="element-208" data-mp="1kpq2t8bzglll">You pay</dt>
              <dd className="tabular text-lg font-bold" data-id="element-209" data-mp="1pokw547ktsjy">{baht(subtotal + serviceFee)}</dd>
            </div>
          </dl>
          <p className="flex items-start gap-2 text-sm text-ink-2" data-id="element-210" data-mp="3mhcwjhn9eulr">
            <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-211" data-mp="3rf4tiq2rh6wt" />
            You’re only charged if they accept. Payment is held safely and released 24 hours after the gig.
          </p>
        </div>}
    </Sheet>;
}